import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'foundations-components/transitional/components/Link';
import CallRecordingPlayer from './CallRecordingPlayer';
import Raven from 'raven-js';
import emptyFunction from 'react-utils/emptyFunction';
import { CommunicatorLogger } from 'calling-usage-tracker/callingTracker';
import DeleteRecordingPrompt from './DeleteRecordingPrompt';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CallRecordingDeleteButton from './CallRecordingDeleteButton';
import { Flex, FlexGrow } from './StyledComponents';
import CallTranscript from './CallTranscript';
import CallMessagePropType from '../operators/CallMessagePropType';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useInterval(callback, delay) {
  const savedCallback = useRef(undefined);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function currentCallback() {
      if (typeof savedCallback.current === 'function') {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const currentInterval = setInterval(currentCallback, delay);
      return function cleanup() {
        clearInterval(currentInterval);
      };
    } else {
      return emptyFunction;
    }
  }, [delay]);
}
const MP3_EXTENSION_REGEX = /(.*)\.mp3$/;
const TWILIO_HOSTED_RECORDING_REGEX = /(.*)twilio.com/;
const HTTPS_REGEX = /^https:\/\//;
const CallRecordingController = ({
  duration,
  engagementId,
  recordingUrl,
  onDeleteRecording,
  callMessage,
  hasVoicemail,
  disableDelete = false
}) => {
  const [actualDuration, setActualDuration] = useState(duration);
  const [isPaused, setIsPaused] = useState(true);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [isSeekable, setIsSeekable] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => {
    // https://issues.hubspotcentral.com/browse/CALLING-6541
    if (audioRef && audioRef.current) {
      audioRef.current.load();
    }
  }, []);
  const handleOnAudioLoaded = useCallback(() => {
    if (!isAudioLoaded) {
      setIsAudioLoaded(true);
    }
    const canSeek = audioRef.current && audioRef.current.seekable.length >= 1;
    if (!canSeek) {
      // In some browsers (e.g. Safari), the Twilio audio clips are unable to be seeked
      // and thus seeking must be disabled (https://issues.hubspotcentral.com/browse/CALLING-896)
      setIsSeekable(false);
      Raven.captureMessage('[Timeline] Call recording seeking failed', {
        extra: {
          engagementId
        }
      });
    }
  }, [engagementId, isAudioLoaded, setIsAudioLoaded, setIsSeekable]);
  const handleSetDuration = useCallback(event => {
    const target = event.currentTarget;
    const _actualDuration = target.duration * 1000;
    if (hasVoicemail && !isNaN(_actualDuration) && isFinite(_actualDuration)) {
      setActualDuration(_actualDuration);
    }
  }, [hasVoicemail]);
  const handlePlayRecording = useCallback(() => {
    const playPromise = audioRef.current && audioRef.current.play();
    if (playPromise) {
      playPromise.then(() => {
        CommunicatorLogger.log('communicator_timelineInteraction', {
          action: 'Listen to call recording',
          activity: 'call',
          channel: 'outbound call'
        });
      }).catch(error => {
        Raven.captureMessage('[Timeline] Call recording play failed', {
          extra: {
            error,
            engagementId
          }
        });
      });
    }
  }, [engagementId]);
  const handlePauseRecording = useCallback(() => {
    if (!audioRef.current) {
      return null;
    }
    const pausePromise = audioRef.current.pause();
    if (pausePromise) {
      pausePromise.catch(error => {
        Raven.captureMessage('[Timeline] Call recording pause failed', {
          extra: {
            error,
            engagementId
          }
        });
      });
    }
    return null;
  }, [engagementId]);
  const handleDeleteRecording = useCallback(() => {
    if (!isPaused) {
      const pausePromise = audioRef.current && audioRef.current.pause();
      if (pausePromise) {
        pausePromise.catch(error => {
          Raven.captureMessage('[Timeline] Call recording pause failed', {
            extra: {
              error,
              engagementId
            }
          });
        });
      }
    }
    setShowDeleteDialog(true);
  }, [engagementId, isPaused]);
  const handleSkipToTime = useCallback(evt => {
    evt.stopPropagation();
    const value = Number(evt.target.value);
    if (isSeekable) {
      const timeToSkipTo = value / 100 * (duration * 0.001);
      if (audioRef.current) {
        audioRef.current.currentTime = timeToSkipTo;
      }
    }
  }, [audioRef, duration, isSeekable]);
  const handleSetIsPlaying = useCallback(() => {
    setIsPaused(false);
  }, [setIsPaused]);
  const handleSetIsPaused = useCallback(() => {
    setIsPaused(true);
  }, [setIsPaused]);
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentPlaybackTime(audioRef.current.currentTime * 1000);
    }
  }, [setCurrentPlaybackTime]);

  // helps to smooth out the "animation" of the slider, as this is called
  // more frequently than the "timeUpdate" event is fired
  useInterval(() => {
    handleTimeUpdate();
  }, isPaused ? null : 20);
  const handleOnEnded = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.load();
    }
    setCurrentPlaybackTime(0);
    setIsAudioLoaded(false);
  }, [setCurrentPlaybackTime, audioRef]);
  const hasMp3Extension = useMemo(() => {
    return MP3_EXTENSION_REGEX.test(recordingUrl);
  }, [recordingUrl]);
  const recordingUrlWAV = useMemo(() => {
    // we should only strip the .mp3 extension for twilio hosted recordings
    // https://issues.hubspotcentral.com/browse/CALLING-5567
    const isTwilioHostedRecording = TWILIO_HOSTED_RECORDING_REGEX.test(recordingUrl);
    if (!isTwilioHostedRecording) {
      return '';
    }

    // removing the .mp3 extension for twilio hosted recordings gives us a
    // WAV version of the recording which should be used as the source for
    // recordings opened in a new window
    // https://issues.hubspotcentral.com/browse/CALLING-896
    if (!hasMp3Extension) {
      return '';
    }
    const regexResult = MP3_EXTENSION_REGEX.exec(recordingUrl);
    if (!regexResult) {
      return '';
    }
    return regexResult[1];
  }, [hasMp3Extension, recordingUrl]);

  // https://issues.hubspotcentral.com/browse/CRM-19699
  const isUrlSecure = HTTPS_REGEX.test(recordingUrlWAV || recordingUrl);
  const handleConfirm = useCallback(() => {
    onDeleteRecording(engagementId);
    setShowDeleteDialog(false);
  }, [onDeleteRecording, setShowDeleteDialog, engagementId]);
  const handleReject = useCallback(() => {
    setShowDeleteDialog(false);
  }, [setShowDeleteDialog]);
  useEffect(() => {
    if (!isUrlSecure) {
      Raven.captureMessage('[Calling] Unsecure call recording', {
        extra: {
          engagementId,
          recordingUrl: recordingUrlWAV || recordingUrl
        }
      });
    }
  });
  const DefaultAudioSource = recordingUrlWAV && /*#__PURE__*/_jsx("source", {
    src: recordingUrlWAV,
    type: "audio/wav",
    "data-test-id": "call-recording-wrapper-source--default"
  });
  const FallbackAudioSource = /*#__PURE__*/_jsx("source", {
    src: recordingUrl,
    type: hasMp3Extension ? 'audio/mpeg' : 'audio/wav',
    "data-test-id": "call-recording-wrapper-source--fallback"
  });
  const ToolTipMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: hasVoicemail ? 'conversations-calling-message.timeline-call-metadata.call-voicemail.external-link-tooltip-message' : 'conversations-calling-message.timeline-call-metadata.call-recording.external-link-tooltip-message'
  });
  const ExternalLink = /*#__PURE__*/_jsx(Tooltip, {
    title: ToolTipMessage,
    children: /*#__PURE__*/_jsx("span", {
      children: /*#__PURE__*/_jsx(Link, {
        external: true,
        href: recordingUrl
      })
    })
  });
  const DeleteButton = /*#__PURE__*/_jsx(CallRecordingDeleteButton, {
    onDeleteRecording: handleDeleteRecording,
    disableDelete: disableDelete
  });
  return /*#__PURE__*/_jsxs(Flex, {
    "data-selenium-test": "call-recording",
    children: [/*#__PURE__*/_jsxs(FlexGrow, {
      children: [/*#__PURE__*/_jsx("audio", {
        "data-test-id": "call-recording-wrapper-audio",
        onCanPlayThrough: handleOnAudioLoaded,
        onEnded: handleOnEnded,
        onLoadedMetadata: handleSetDuration,
        onPause: handleSetIsPaused,
        onPlaying: handleSetIsPlaying,
        onTimeUpdate: handleTimeUpdate,
        preload: "auto",
        ref: audioRef,
        src: undefined,
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [DefaultAudioSource, FallbackAudioSource]
        })
      }), /*#__PURE__*/_jsx(CallRecordingPlayer, {
        currentPlaybackTime: currentPlaybackTime,
        duration: actualDuration,
        isLoading: !isAudioLoaded,
        isPaused: isPaused,
        canSeek: isSeekable,
        onPauseRecording: handlePauseRecording,
        onPlayRecording: handlePlayRecording,
        onSkipToTime: handleSkipToTime,
        hasVoicemail: hasVoicemail
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      children: [DeleteButton, ExternalLink, /*#__PURE__*/_jsx(CallTranscript, {
        callMessage: callMessage
      })]
    }), showDeleteDialog && /*#__PURE__*/_jsx(DeleteRecordingPrompt, {
      onConfirm: handleConfirm,
      onReject: handleReject,
      hasVoicemail: hasVoicemail
    })]
  });
};
CallRecordingController.propTypes = {
  duration: PropTypes.number.isRequired,
  engagementId: PropTypes.number.isRequired,
  recordingUrl: PropTypes.string.isRequired,
  onDeleteRecording: PropTypes.func.isRequired,
  callMessage: CallMessagePropType.isRequired,
  hasVoicemail: PropTypes.bool
};
export default CallRecordingController;